import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaBell } from 'react-icons/fa';
import { useNotification } from '../context/NotificationContext';
import NotificationList from '../components/Notifications/NotificationList';
import { CATEGORY_METADATA } from '../api/notifications';
import styles from './NotificationsPage.module.css';

/**
 * NotificationsPage Component
 * 
 * Dedicated page for comprehensive notification management with:
 * - Full notification list with advanced filtering
 * - Category-based navigation
 * - Bulk actions
 * - Settings access
 * - Statistics overview
 */
const NotificationsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [totalCountByCategory, setTotalCountByCategory] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  
  const { 
    unreadCount, 
    unreadCountByCategory, 
    markAllAsRead,
    preferences,
    fetchNotifications,
    fetchCategoryBreakdown
  } = useNotification();

  // Fetch category breakdown on mount (uses dedicated method instead of fetching 1000 items)
  useEffect(() => {
    const fetchCategoryCounts = async () => {
      try {
        await fetchCategoryBreakdown();
        // Use a lightweight count endpoint instead of fetching all notifications
        const response = await fetchNotifications({ limit: 1, offset: 0 });
        const total = response.pagination?.total || 0;
        setTotalCount(total);
        
        // Build category counts from unread counts (approximate until backend provides total counts)
        setTotalCountByCategory(unreadCountByCategory);
      } catch {
        // Silently handle
      }
    };
    
    fetchCategoryCounts();
  }, [fetchNotifications, fetchCategoryBreakdown]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle category selection
   */
  const handleCategorySelect = (category) => {
    setActiveCategory(category);
  };

  /**
   * Handle mark all as read for current category
   */
  const handleMarkAllRead = async () => {
    const typeFilter = activeCategory !== 'all' ? activeCategory : null;
    await markAllAsRead(typeFilter);
  };

  /**
   * Navigate back
   */
  const handleBackClick = () => {
    navigate(-1);
  };

  // Category options with counts (showing total count, with unread indicator)
  const categoryOptions = [
    { 
      key: 'all', 
      name: 'All Notifications', 
      icon: '📋', 
      count: totalCount,
      unreadCount: unreadCount,
      description: 'View all notifications across all categories'
    },
    ...Object.entries(CATEGORY_METADATA).map(([key, meta]) => ({
      key,
      name: meta.name,
      icon: meta.icon,
      count: totalCountByCategory[key] || 0,
      unreadCount: unreadCountByCategory[key] || 0,
      description: meta.description
    }))
  ];

  return (
    <div className={styles.notificationsPage}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <button
            className={styles.backButton}
            onClick={handleBackClick}
            aria-label="Go back"
          >
            <FaArrowLeft />
          </button>
          <div className={styles.titleSection}>
            <h1 className={styles.pageTitle}>
              <FaBell className={styles.titleIcon} />
              Notifications
            </h1>
            {unreadCount > 0 && (
              <span className={styles.unreadBadge}>
                {unreadCount} unread
              </span>
            )}
          </div>
        </div>
        
        <div className={styles.headerActions}>
          {unreadCount > 0 && (
            <button
              className={styles.markAllReadButton}
              onClick={handleMarkAllRead}
              title="Mark all as read"
            >
              Mark All Read
            </button>
          )}
        </div>
      </div>

      <div className={styles.pageContent}>
        {/* Sidebar - Category Navigation */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h3>Categories</h3>
          </div>
          
          <nav className={styles.categoryNav} role="navigation" aria-label="Notification categories">
            {categoryOptions.map(category => (
              <button
                key={category.key}
                className={`${styles.categoryButton} ${activeCategory === category.key ? styles.active : ''} ${category.unreadCount > 0 ? styles.hasUnread : ''}`}
                onClick={() => handleCategorySelect(category.key)}
                title={category.description}
                aria-label={`${category.name} (${category.count} total, ${category.unreadCount} unread)`}
              >
                <div className={styles.categoryContent}>
                  <div className={styles.categoryLeft}>
                    <span className={styles.categoryIcon}>{category.icon}</span>
                    <span className={styles.categoryName}>{category.name}</span>
                  </div>
                  {category.count > 0 && (
                    <span className={`${styles.categoryCount} ${category.unreadCount > 0 ? styles.hasUnreadCount : ''}`}>
                      {category.count > 99 ? '99+' : category.count}
                    </span>
                  )}
                </div>
              </button>
            ))}
          </nav>

          {/* Quick Stats */}
          <div className={styles.quickStats}>
            <h4>Quick Stats</h4>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Total Unread:</span>
              <span className={styles.statValue}>{unreadCount}</span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Language:</span>
              <span className={styles.statValue}>
                {preferences?.preferredLanguage?.toUpperCase() || 'EN'}
              </span>
            </div>
            <div className={styles.statItem}>
              <span className={styles.statLabel}>Email Enabled:</span>
              <span className={styles.statValue}>
                {preferences?.channelPreferences?.email ? '✅' : '❌'}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={styles.mainContent}>
          {/* Category header */}
          {activeCategory !== 'all' && (
            <div className={styles.categoryHeader}>
              <div className={styles.categoryInfo}>
                <span className={styles.categoryHeaderIcon}>
                  {CATEGORY_METADATA[activeCategory]?.icon}
                </span>
                <div>
                  <h2 className={styles.categoryTitle}>
                    {CATEGORY_METADATA[activeCategory]?.name}
                  </h2>
                  <p className={styles.categoryDescription}>
                    {CATEGORY_METADATA[activeCategory]?.description}
                  </p>
                </div>
              </div>
              
              <div className={styles.categoryStats}>
                {(totalCountByCategory[activeCategory] || 0) > 0 ? (
                  <span className={styles.categoryUnread}>
                    {totalCountByCategory[activeCategory]} notification{totalCountByCategory[activeCategory] !== 1 ? 's' : ''}
                    {(unreadCountByCategory[activeCategory] || 0) > 0 && (
                      <span className={styles.unreadIndicator}> ({unreadCountByCategory[activeCategory]} unread)</span>
                    )}
                  </span>
                ) : (
                  <span className={styles.categoryEmpty}>No notifications</span>
                )}
              </div>
            </div>
          )}

          {/* Notification List */}
          <NotificationList
            category={activeCategory === 'all' ? null : activeCategory}
            showFilters={true}
            showSearch={false} // We have search in header
            showBulkActions={true}
            className={styles.notificationListContainer}
          />
        </div>
      </div>
    </div>
  );
};

export default NotificationsPage;
